import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  ChevronRight,
  ExternalLink,
  MessageCircle,
  Percent,
  Wallet,
} from 'lucide-react';
import api from '../../services/api.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import simulatorConfig from '../../content/creditSimulator.json';
import publicCta from '../../content/publicCta.json';
import { getWhatsAppUrl } from '../../constants/whatsappContact.js';
import {
  formatAmountWithDots,
  formatCop,
  parseAmountDigits,
  parseMaxMonthsFromText,
  parseRatePercentFromText,
  simulateCredit,
} from '../../utils/creditSimulator.js';

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#1acb12] focus:ring-2 focus:ring-[#1acb12]/25';

function buildWhatsAppMessage({ lineTitle, amount, months, rate, result }) {
  const intro = simulatorConfig.whatsappIntro || publicCta.whatsappTopics?.find((t) => t.id === 'creditos')?.message;
  const parts = [
    intro,
    lineTitle ? `Línea de interés: ${lineTitle}.` : null,
    `Monto simulado: ${formatCop(amount)}.`,
    `Plazo: ${months} meses.`,
    `Tasa mensual: ${rate}%.`,
    result
      ? `Cuota mensual fija: ${formatCop(result.monthlyPayment)}. Seguro de vida total (${result.lifeInsurancePercent}% mensual sobre saldo): ${formatCop(result.lifeInsurance)}. Total a pagar: ${formatCop(result.grandTotal)}.`
      : null,
  ].filter(Boolean);
  return parts.join(' ');
}

export default function CreditosSimuladorPage() {
  const [lines, setLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(true);

  const [lineId, setLineId] = useState('');
  const [amount, setAmount] = useState(String(simulatorConfig.defaultAmount ?? 10000000));
  const [months, setMonths] = useState(String(simulatorConfig.defaultMonths ?? 36));
  const [monthlyRate, setMonthlyRate] = useState(String(simulatorConfig.defaultMonthlyRate ?? 1.2));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingLines(true);
      try {
        const { data: res } = await api.get('/v1/public/creditos');
        if (!cancelled) {
          setLines(Array.isArray(res?.data) ? res.data : []);
        }
      } finally {
        if (!cancelled) setLoadingLines(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedLine = useMemo(
    () => lines.find((l) => String(l.id) === String(lineId)),
    [lines, lineId]
  );

  const selectedLineRate = useMemo(
    () => (selectedLine ? parseRatePercentFromText(selectedLine.tasa_interes) : null),
    [selectedLine]
  );

  function applyLineDefaults(line) {
    if (!line) return;
    const parsedRate = parseRatePercentFromText(line.tasa_interes);
    const parsedMonths = parseMaxMonthsFromText(line.plazo);
    if (parsedRate != null) setMonthlyRate(String(parsedRate));
    if (parsedMonths != null) {
      setMonths(String(Math.min(parsedMonths, simulatorConfig.maxMonths ?? 180)));
    }
    setResult(null);
    setError(null);
  }

  function onLineChange(e) {
    const id = e.target.value;
    setLineId(id);
    const line = lines.find((l) => String(l.id) === String(id));
    if (line) applyLineDefaults(line);
  }

  function onAmountChange(e) {
    const digits = parseAmountDigits(e.target.value);
    if (!digits) {
      setAmount('');
      return;
    }
    const max = Number(simulatorConfig.maxAmount) || 500000000;
    const n = Math.min(Number(digits), max);
    setAmount(String(n));
  }

  function onSimulate(e) {
    e.preventDefault();
    setShowTable(false);
    const sim = simulateCredit({
      amount,
      months,
      monthlyRatePercent: monthlyRate,
      lifeInsurancePercent: simulatorConfig.lifeInsurancePercent ?? 0.2,
    });
    if (sim.error) {
      setError(sim.error);
      setResult(null);
      return;
    }
    setError(null);
    setResult(sim);
  }

  const whatsappHref = result
    ? getWhatsAppUrl(
        buildWhatsAppMessage({
          lineTitle: selectedLine?.titulo,
          amount,
          months,
          rate: monthlyRate,
          result,
        })
      )
    : null;

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        backTo="/creditos"
        backLabel="Volver a créditos"
        eyebrow="Herramientas"
        title="Simulador de crédito"
        description="Calcula una cuota mensual estimada según monto, plazo e interés."
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid min-w-0 gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <PremiumCard variant="light" className="!p-6 sm:!p-8">
                <div className="mb-6 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0d2b7f]/10 text-[#0d2b7f] ring-1 ring-[#0d2b7f]/15">
                    <Calculator className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-[#0d2b7f]">Datos de la simulación</h2>
                    <p className="text-sm text-slate-600">Completa los campos y pulsa calcular.</p>
                  </div>
                </div>

                <form className="space-y-5" onSubmit={onSimulate}>
                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Línea de crédito (opcional)
                    </span>
                    <select
                      className={inputClass}
                      value={lineId}
                      onChange={onLineChange}
                      disabled={loadingLines}
                    >
                      <option value="">Seleccionar línea…</option>
                      {lines.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.titulo}
                        </option>
                      ))}
                    </select>
                    {selectedLine?.tasa_interes ? (
                      <p className="text-xs text-slate-500">
                        Referencia publicada: {selectedLine.tasa_interes}
                        {selectedLine.plazo ? ` · ${selectedLine.plazo}` : ''}
                        {selectedLineRate != null
                          ? ` · Tasa aplicada en el simulador: ${selectedLineRate}% mensual`
                          : ''}
                      </p>
                    ) : null}
                  </label>

                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Monto a financiar (COP)
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className={inputClass}
                      placeholder="Ej. 10.000.000"
                      value={amount ? formatAmountWithDots(amount) : ''}
                      onChange={onAmountChange}
                      required
                    />
                  </label>

                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Plazo (meses)
                    </span>
                    <input
                      type="number"
                      className={inputClass}
                      min={simulatorConfig.minMonths}
                      max={simulatorConfig.maxMonths}
                      value={months}
                      onChange={(e) => setMonths(e.target.value)}
                      required
                    />
                  </label>

                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Interés mensual (%)
                    </span>
                    <input
                      type="number"
                      className={inputClass}
                      min={0}
                      max={100}
                      step={0.01}
                      value={monthlyRate}
                      onChange={(e) => setMonthlyRate(e.target.value)}
                      required
                    />
                  </label>

                  <p className="rounded-lg border border-[#0d2b7f]/15 bg-[#0d2b7f]/5 px-3 py-2.5 text-xs leading-relaxed text-slate-600">
                    <span className="font-semibold text-[#0d2b7f]">
                      {simulatorConfig.lifeInsuranceLabel ?? 'Seguro de vida (obligatorio)'}:
                    </span>{' '}
                    {simulatorConfig.lifeInsuranceNote ??
                      'Se calcula cada mes: 0,2% del saldo inicial de la cuota.'}
                  </p>

                  {error ? (
                    <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0d2b7f] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0a2266]"
                  >
                    <Calculator className="h-4 w-4" aria-hidden />
                    Calcular cuota
                  </button>
                </form>

                <p className="mt-5 text-xs leading-relaxed text-slate-500" role="note">
                  {simulatorConfig.disclaimer}
                </p>
              </PremiumCard>
            </div>

            <div className="min-w-0 space-y-6 lg:col-span-7">
              {!result && !error && (
                <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                  <Percent className="h-10 w-10 text-[#1acb12]" aria-hidden />
                  <p className="mt-4 max-w-sm text-sm text-slate-600">
                    Ingresa los datos y calcula para ver la cuota mensual estimada y el plan de pagos.
                  </p>
                </div>
              )}

              {result && (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <PremiumCard variant="light" className="!p-4 sm:!p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cuota mensual</p>
                      <p className="mt-1 text-[10px] text-slate-500">Igual todos los meses (capital + interés + seguro)</p>
                      <p className="mt-2 font-display text-lg font-bold leading-tight text-[#0d2b7f] sm:text-xl lg:text-2xl">
                        <span className="block break-words tabular-nums">{formatCop(result.firstMonthTotal)}</span>
                      </p>
                    </PremiumCard>
                    <PremiumCard variant="light" className="!p-4 sm:!p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total intereses</p>
                      <p className="mt-2 font-display text-lg font-bold leading-tight text-slate-800 sm:text-xl lg:text-2xl">
                        <span className="block break-words tabular-nums">{formatCop(result.totalInterest)}</span>
                      </p>
                    </PremiumCard>
                    <PremiumCard variant="light" className="!p-4 sm:!p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Seguro de vida ({result.lifeInsurancePercent}%)
                      </p>
                      <p className="mt-1 text-[10px] text-slate-500">Obligatorio · 0,2% del saldo cada mes (total del plazo)</p>
                      <p className="mt-2 font-display text-lg font-bold leading-tight text-slate-800 sm:text-xl lg:text-2xl">
                        <span className="block break-words tabular-nums">{formatCop(result.lifeInsurance)}</span>
                      </p>
                    </PremiumCard>
                    <PremiumCard variant="light" className="!p-4 sm:!p-5 ring-1 ring-[#0d2b7f]/15">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Total a pagar (cuotas + seguro)
                      </p>
                      <p className="mt-1 text-[10px] text-slate-500">
                        Cuotas: {formatCop(result.totalPaid)} + seguro: {formatCop(result.lifeInsurance)}
                      </p>
                      <p className="mt-2 font-display text-lg font-bold leading-tight text-[#0d2b7f] sm:text-xl lg:text-2xl">
                        <span className="block break-words tabular-nums">{formatCop(result.grandTotal)}</span>
                      </p>
                    </PremiumCard>
                  </div>

                  <PremiumCard variant="light" className="min-w-0 !p-4 sm:!p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="font-display text-base font-semibold text-[#0d2b7f] sm:text-lg">
                        Plan de amortización
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowTable((v) => !v)}
                        className="w-full rounded-lg border border-[#1acb12]/35 bg-[#1acb12]/8 px-4 py-2.5 text-sm font-semibold text-[#0d2b7f] transition hover:bg-[#1acb12]/15 sm:w-auto sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:text-[#1acb12] sm:hover:bg-transparent sm:hover:text-[#0d2b7f]"
                      >
                        {showTable ? 'Ocultar tabla' : 'Ver tabla de cuotas'}
                      </button>
                    </div>

                    {showTable && (
                      <div className="mt-4 min-w-0 max-w-full space-y-2">
                        <p className="text-center text-xs font-medium text-slate-500 md:hidden">
                          ← Desliza horizontalmente para ver todas las columnas →
                        </p>
                        <div className="max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                          <div
                            className="credit-amort-scroll max-h-80 max-w-full overflow-x-scroll overflow-y-auto overscroll-x-contain"
                            tabIndex={0}
                            role="region"
                            aria-label="Tabla de amortización, desplazamiento horizontal"
                          >
                            <table className="w-full min-w-[44rem] border-collapse divide-y divide-slate-200 text-left text-xs sm:text-sm">
                              <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-[#0d2b7f] sm:text-xs">
                                <tr>
                                  <th className="whitespace-nowrap px-3 py-2.5">#</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Saldo inicial</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Interés</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Capital</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Seguro vida</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Total cuota</th>
                                  <th className="whitespace-nowrap px-3 py-2.5">Saldo</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                                {result.schedule.map((row) => (
                                  <tr key={row.period} className="hover:bg-slate-50/80">
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{row.period}</td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                      {formatCop(row.openingBalance)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                      {formatCop(row.interest)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                      {formatCop(row.capital)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                      {formatCop(row.lifeInsurance)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium text-[#0d2b7f]">
                                      {formatCop(row.totalPayment)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                      {formatCop(row.balance)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </PremiumCard>

                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    {whatsappHref ? (
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#25D366]/50 bg-[#25D366] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#20bd5a] sm:w-auto sm:py-2.5"
                      >
                        <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="text-center">{publicCta.creditosWhatsAppLabel}</span>
                        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                      </a>
                    ) : null}
                    <Link
                      to="/creditos"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#0d2b7f]/30 bg-white px-5 py-3 text-sm font-semibold text-[#0d2b7f] shadow-sm transition hover:bg-slate-50 sm:w-auto sm:py-2.5"
                    >
                      <Wallet className="h-4 w-4 shrink-0" aria-hidden />
                      Ver líneas de crédito
                      <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {loadingLines && (
            <BrandLoading variant="inline" tone="light" label="Cargando líneas de crédito…" className="mt-8" />
          )}
        </div>
      </section>
    </div>
  );
}
